import { CheckCircle2, AlertTriangle, X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

type ToastTone = "success" | "error";

interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  toast: (message: Omit<ToastMessage, "id">) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);
  const counter = React.useRef(0);

  const dismiss = React.useCallback((id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const toast = React.useCallback(
    (message: Omit<ToastMessage, "id">) => {
      const id = ++counter.current;
      setMessages((prev) => [...prev, { ...message, id }]);
      window.setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const value = React.useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (title, description) => toast({ title, description, tone: "success" }),
      error: (title, description) => toast({ title, description, tone: "error" }),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
        role="region"
        aria-live="polite"
      >
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "pointer-events-auto flex items-start gap-2.5 rounded-lg border border-border bg-card p-3 shadow-lg animate-slide-up",
            )}
          >
            {m.tone === "success" ? (
              <CheckCircle2
                className="mt-px size-4 shrink-0"
                style={{ color: "var(--status-good)" }}
              />
            ) : (
              <AlertTriangle
                className="mt-px size-4 shrink-0"
                style={{ color: "var(--status-critical)" }}
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{m.title}</p>
              {m.description && (
                <p className="mt-0.5 break-words text-xs text-muted-foreground">
                  {m.description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(m.id)}
              className="rounded p-0.5 text-muted-foreground hover:bg-accent"
              aria-label="Dismiss"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
